import { parseDate } from "@lyfegen-platform/utils";
import { Box, makeStyles, MenuItem, Select, Typography } from "@material-ui/core";
import { TimelineDot } from "@material-ui/lab";
import { endOfDay } from "date-fns";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import DatePicker from "../../../../../components/date-range-picker/DatePicker";
import LoadingBox from "../../../../../components/LoadingBox";
import LyfeTimeline from "../../../../../components/LyfeTimeline";
import { blueContrast, blueContrastWithOpacity } from "../../../../../hooks/use-map-theme-styles";
import { useDataMutation, useDataQuery } from "../../../../../hooks/useDataModule";
import { Maybe } from "../../../../../models/common/generic-types";
import { CaseEventCategory } from "../../../../../models/generated/graphql";
import { userPersonalInfoState } from "../../../../../store/user";
import isJsonString from "../../../../../utils/is-json-string";
import { localeDateTime } from "../../../../../utils/locale-date-time";
import { GET_CASE_EVENT_LOG_FILTERS, GET_CASE_EVENT_LOGS_BY_CASE_ID, UPDATE_CASE_EVENT_LOG_FILTER } from "../../../queries";
import { GetSanitizedCaseEventLogsQuery, GetUserContextFiltersCaseEventLogsQuery } from "../../../types/queries.generated";
import { mapEventCategoryToColor } from "./mapper";
import { getEventDescriptionString } from "./message-parser";

const defaultFilters = {
	selectedCategories: [CaseEventCategory.DataAddition, CaseEventCategory.FinanciallyRelevant, CaseEventCategory.Negotiation],
	selectedDateRange: [new Date(subtractMonth()), new Date()],
};

function subtractMonth(date = new Date()) {
	date.setMonth(date.getMonth() - 1);

	return date;
}

export function CaseEventLogs(props: { caseId: string; currencyCode: Maybe<string> }) {
	const { t } = useTranslation();
	const styles = useStyles();
	const userData = useRecoilValue(userPersonalInfoState);
	const [caseEventLogs, setCaseEventLogs] = useState<GetSanitizedCaseEventLogsQuery["GetSanitizedCaseEventLogs"]>([]);
	const [filteredCaseEventLogs, setFilteredCaseEventLogs] = useState<GetSanitizedCaseEventLogsQuery["GetSanitizedCaseEventLogs"]>([]);
	const [selectedCategories, setSelectedCategories] = useState<Array<CaseEventCategory>>(
		defaultFilters.selectedCategories as unknown as Array<CaseEventCategory>
	);
	const [selectedDateRange, setSelectedDateRange] = useState<[Maybe<Date>, Maybe<Date>]>(
		defaultFilters.selectedDateRange as [Maybe<Date>, Maybe<Date>]
	);
	const caseEventCategoryOptions = [
		CaseEventCategory.Chat,
		CaseEventCategory.DataAddition,
		CaseEventCategory.FinanciallyRelevant,
		CaseEventCategory.Negotiation,
		CaseEventCategory.System,
	];
	const setFilterValues = (serializedFilters: string | null | undefined) => {
		const parsedFilters = serializedFilters && isJsonString(serializedFilters) ? JSON.parse(serializedFilters) : defaultFilters;

		setSelectedCategories(parsedFilters.selectedCategories);
		setSelectedDateRange(parsedFilters.selectedDateRange.map((dateString: string) => (dateString ? parseDate(dateString) : null)));
	};

	const { data: _, loading } = useDataQuery<GetSanitizedCaseEventLogsQuery>(GET_CASE_EVENT_LOGS_BY_CASE_ID, {
		variables: { caseId: props.caseId },
		onCompleted: (data: GetSanitizedCaseEventLogsQuery) => setCaseEventLogs(data.GetSanitizedCaseEventLogs || []),
	});

	const { data: __, loading: filterLoading } = useDataQuery<GetUserContextFiltersCaseEventLogsQuery>(GET_CASE_EVENT_LOG_FILTERS, {
		onCompleted: (queryData) => setFilterValues(queryData.filters),
	});

	const [mutateFilters] = useDataMutation(UPDATE_CASE_EVENT_LOG_FILTER, {
		refetchQueries: [
			{
				query: GET_CASE_EVENT_LOG_FILTERS,
			},
		],
	});

	useEffect(() => {
		setFilteredCaseEventLogs(
			caseEventLogs
				.filter((caseEventLog) => selectedCategories.includes(caseEventLog.eventCategory as unknown as CaseEventCategory))
				.filter(
					(caseEventLog) =>
						!selectedDateRange[0] ||
						!selectedDateRange[1] ||
						(parseDate(caseEventLog.triggeredOn) >= selectedDateRange[0] &&
							parseDate(caseEventLog.triggeredOn) <= selectedDateRange[1])
				)
		);
	}, [selectedCategories, caseEventLogs, selectedDateRange]);

	if (loading || filterLoading) {
		return <LoadingBox fullHeight />;
	}

	const handleSetSelectedCategories = (selectedEvent: any) => {
		selectedEvent.preventDefault();

		const {
			target: { value },
		} = selectedEvent;

		setSelectedCategories(value);
		mutateFilters({
			variables: {
				req: JSON.stringify({ selectedCategories: value, selectedDateRange: selectedDateRange }),
			},
		});
	};

	const handleDateRangeFilter = (startDate: Maybe<Date>, endDate: Maybe<Date>) => {
		const endOfDayDate = endDate ? endOfDay(endDate) : endDate;
		setSelectedDateRange([startDate, endOfDayDate]);
		mutateFilters({
			variables: {
				req: JSON.stringify({
					selectedCategories: selectedCategories,
					selectedDateRange: [startDate, endOfDayDate],
				}),
			},
		});
	};

	return (
		<Box width="100%">
			<Box width="100%" display="flex" flexDirection="column" paddingBottom="125px">
				<Box width="100%" display="flex" flexDirection="row" style={{ verticalAlign: "center" }}>
					<Box mr={3}>
						<DatePicker
							{...(!!selectedDateRange[0] && { startDate: selectedDateRange[0] })}
							{...(!!selectedDateRange[1] && { endDate: selectedDateRange[1] })}
							isCaseEventLogFilter={true}
							onChangeRange={handleDateRangeFilter}
							labelBg="#fff"
							checkDate={true}
						/>
					</Box>
					<Select
						id="case-log-categories"
						style={{ width: "30%", height: "50px" }}
						variant="outlined"
						multiple
						className={styles.select}
						value={selectedCategories}
						onChange={handleSetSelectedCategories}
						renderValue={(values) => (
							<Box display="flex" flexDirection="row">
								{(values as Array<CaseEventCategory>).map((value) => {
									const colors = mapEventCategoryToColor(value);

									return (
										value && (
											<TimelineDot
												style={{ backgroundColor: colors[0], borderColor: colors[1], marginRight: "5px" }}
											/>
										)
									);
								})}
							</Box>
						)}
					>
						{caseEventCategoryOptions.map((option, index) => {
							const colors = mapEventCategoryToColor(option as CaseEventCategory);

							return (
								<MenuItem key={index} value={option}>
									<Box display="flex" flexDirection="row" alignItems="center">
										<TimelineDot style={{ backgroundColor: colors[0], borderColor: colors[1], marginRight: "5px" }} />
										<Typography style={{ fontSize: "16px" }}>{t(option)}</Typography>
									</Box>
								</MenuItem>
							);
						})}
					</Select>
				</Box>
				{filteredCaseEventLogs.length ? (
					<LyfeTimeline
						items={filteredCaseEventLogs.map((caseEventLog) => ({
							child: (
								<Box>
									<Typography>
										{getEventDescriptionString(caseEventLog, t, userData.organization.externalId, props.currencyCode)}
									</Typography>
									<Typography className={styles.dateContainer}>
										{localeDateTime(new Date(caseEventLog.triggeredOn))}
									</Typography>
								</Box>
							),
							timelineDotColors: mapEventCategoryToColor(caseEventLog.eventCategory),
						}))}
						connectorColor={blueContrast}
					/>
				) : (
					<Box alignSelf={"center"} marginTop="40px">
						<Typography>{t("no-data")}</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
}

const useStyles = makeStyles(() => ({
	verticalLine: {
		width: "2px",
		height: "607px",
		margin: "28px 17px 0 24.5px",
		backgroundColor: "#cddcdc",
	},
	dateContainer: {
		marginTop: "4px",
		fontSize: "12px",
		color: "#969392",
		fontWeight: 300,
		letterSpacing: "0.15px",
	},
	caseIdColumn: {
		width: "30%",
	},
	refundColumn: {
		width: "20%",
	},
	ratingColumn: {
		width: "20%",
	},
	row: {
		padding: "10px",
		"&:nth-child(odd)": {
			backgroundColor: blueContrastWithOpacity,
		},
	},
	select: {
		"& .MuiSelect-select:focus": {
			backgroundColor: "#fff",
		},
	},
}));
